import { throttle } from "lodash";
import * as Sentry from "@sentry/react";

class CommentsManager {
  constructor() {
    this.comments = new Map();
    this.marks = new Map();
    this.newCommentBox = null;
    this.newCommentBoxY = null;
    this.throttledUpdate = throttle(this.updateAllPositions, 16);
    this.resizeObserver = new ResizeObserver(this.throttledUpdate);
    this.mutationObserver = new MutationObserver(() => {
      this.validateAndUpdateMarks();
      this.throttledUpdate();
    });
    this.documentRef = null;
    this.documentId = null;
    this.offset = 0;
    this.totalCommentsHeight = 0;
    this.heightChangeCallbacks = [];
    this.avoidOverlaps = true;
  }

  findMarkElement(annotationId) {
    if (!this.documentRef) return null;
    return this.documentRef.querySelector(`span[data-annotation-id*="${annotationId}"]`);
  }

  handleCommentMarkNotFound(commentEl) {
    commentEl.style.display = "none";
  }

  registerMark(id) {
    this.marks.set(id, { matched: false });
  }

  unregisterMark(id) {
    this.marks.delete(id);
  }

  resetMarks() {
    this.marks.clear();
  }

  validateAndUpdateMarks() {
    if (!this.documentRef) return;
    try {
      for (const [id, data] of this.comments.entries()) {
        if (!this.documentRef.contains(data.mark)) {
          const newMark = this.findMarkElement(data.comment.getAttribute("data-annotation-id"));
          if (newMark) {
            this.comments.set(id, { ...data, mark: newMark });
          } else {
            this.handleCommentMarkNotFound(data.comment);
          }
        } else {
          data.comment.style.display = "";
        }
      }
    } catch (error) {
      Sentry.captureMessage("Error validateAndUpdateMarks", {
        extra: {
          error,
        },
      });
    }
  }

  registerComment(commentEl, annotationId, id) {
    const markEl = this.findMarkElement(annotationId);
    if (!markEl) {
      commentEl.style.display = "none";
      this.handleCommentMarkNotFound(commentEl);
      return;
    }

    this.comments.set(id, { comment: commentEl, mark: markEl });
    this.resizeObserver.observe(commentEl);
  }

  unregisterComment(id) {
    const comment = this.comments.get(id);
    if (comment) {
      this.resizeObserver.unobserve(comment.comment);
      this.comments.delete(id);
    }
  }

  registerNewCommentBox(commentEl, y) {
    this.newCommentBox = commentEl;
    const editorElement = this.documentRef?.querySelector(".EditorWrapper");
    if (editorElement) {
      // takes effect in the share link page
      const editorTop = editorElement.getBoundingClientRect().top;
      this.newCommentBoxY = y - editorTop;
    } else {
      // takes effect in the editor
      this.newCommentBoxY = y - 10;
    }
    this.resizeObserver.observe(commentEl);
    this.throttledUpdate();
  }

  unregisterNewCommentBox() {
    if (this.newCommentBox) {
      this.resizeObserver.unobserve(this.newCommentBox);
      this.newCommentBox = null;
      this.newCommentBoxY = null;
      this.throttledUpdate();
    }
  }

  /**
   * Register a callback that will be called when comment heights change
   * @param {Function} callback - Function to call with new height
   * @returns {Function} - Function to unregister the callback
   */
  onHeightChange(callback) {
    this.heightChangeCallbacks.push(callback);
    return () => {
      this.heightChangeCallbacks = this.heightChangeCallbacks.filter((cb) => cb !== callback);
    };
  }

  /**
   * Get the total height needed to accommodate all comments
   * @returns {number} - The total height in pixels
   */
  getTotalHeight() {
    return this.totalCommentsHeight;
  }

  /**
   * Updates the positions of all comments and the new comment box
   * General Algorithm:
   * 1. Create an array of all comments plus the new comment box if it exists
   * 2. Sort the array by the mark position
   * 3. Place each item, always checking if the current place would overlap with the new comment box
   * 4. If it does, shift all placed comments up by the amount of overlap and place the new comment box
   * 5. Otherwise, place all items as usual
   */
  updateAllPositions = () => {
    if (this.comments.size === 0 && !this.newCommentBox) {
      this.totalCommentsHeight = 0;
      this.notifyHeightChange();
      return;
    }

    try {
      const sortedItems = this.getSortedCommentItems();
      this.placeAllComments(sortedItems);

      // Calculate total height after placement
      this.calculateTotalHeight();
    } catch (error) {
      Sentry.captureMessage("Error updateAllPositions", {
        extra: { error },
      });
    }
  };

  getSortedCommentItems() {
    // Convert existing comments to sorted items
    const items = Array.from(this.comments.values()).map(({ comment, mark }) => ({
      element: comment,
      mark,
      markTop: mark ? mark.getBoundingClientRect().top + window.scrollY + this.offset : Infinity,
      isNewComment: false,
    }));

    // Add new comment box if it exists
    if (this.newCommentBox) {
      items.push({
        element: this.newCommentBox,
        mark: null,
        markTop: this.newCommentBoxY,
        isNewComment: true,
      });
    }

    return items.sort((a, b) => a.markTop - b.markTop);
  }

  placeAllComments(sortedItems) {
    let lastBottom = -Infinity;
    const placedComments = [];
    let newCommentPlaced = false;

    for (const item of sortedItems) {
      // Skip if we're trying to place new comment box that's already placed
      if (item.isNewComment && newCommentPlaced) continue;

      const newTop = this.avoidOverlaps ? Math.max(item.markTop, lastBottom + 10) : item.markTop;

      // Check if we need to handle overlap with new comment box
      if (!this.avoidOverlaps && this.shouldHandleNewCommentOverlap(newCommentPlaced, lastBottom)) {
        this.handleNewCommentOverlap(placedComments);
        newCommentPlaced = true;
        lastBottom = this.newCommentBoxY + this.newCommentBox.offsetHeight;

        // Place current item if it's not the new comment box
        if (!item.isNewComment) {
          this.placeComment(item.element, Math.max(item.markTop, lastBottom + 10));
          lastBottom = parseInt(item.element.style.top) + item.element.offsetHeight;
          placedComments.push(item.element);
        }
        continue;
      }

      // Otherwise, place regular comment
      this.placeComment(item.element, newTop);
      if (item.isNewComment) {
        newCommentPlaced = true;
      }
      lastBottom = newTop + item.element.offsetHeight;
      placedComments.push(item.element);
    }
  }

  shouldHandleNewCommentOverlap(newCommentPlaced, lastBottom) {
    return this.newCommentBox && !newCommentPlaced && lastBottom > this.newCommentBoxY;
  }

  handleNewCommentOverlap(placedComments) {
    const MINIMUM_GAP = 10;

    // Shift positions up beginning from the bottom until no more overlaps
    for (let i = placedComments.length - 1; i >= 0; i--) {
      const currentComment = placedComments[i];

      // Calculate the bottom of the current comment
      const currentPosition = {
        top: parseInt(currentComment.style.top),
        height: currentComment.offsetHeight,
      };
      const currentBottom = currentPosition.top + currentPosition.height;

      // The next comment is either the new comment box (if this is the last placed comment)
      // or the next comment in the list
      const isLastPlacedComment = i === placedComments.length - 1;
      const nextComment = isLastPlacedComment ? this.newCommentBox : placedComments[i + 1];
      const nextCommentTop = parseInt(nextComment.style.top);

      // Check if the current comment overlaps with the next comment
      const hasOverlap = currentBottom + MINIMUM_GAP > nextCommentTop;
      if (!hasOverlap) {
        // If there's no overlap, all comments above this one are properly positioned
        break;
      }

      // Calculate and apply the minimum shift needed to prevent overlap
      const requiredShift = currentBottom + MINIMUM_GAP - nextCommentTop;
      currentComment.style.top = `${currentPosition.top - requiredShift}px`;
    }

    // Place the new comment box at its designated position
    this.newCommentBox.style.top = `${this.newCommentBoxY}px`;
  }

  /**
   * Calculate the total height needed for all comments
   */
  calculateTotalHeight() {
    let maxBottom = 0;

    // Check each comment to find the one that extends furthest down
    for (const { comment } of this.comments.values()) {
      const top = parseInt(comment.style.top) || 0;
      const height = comment.offsetHeight || 0;
      const bottom = top + height;

      if (bottom > maxBottom) {
        maxBottom = bottom;
      }
    }

    // Also check the new comment box
    if (this.newCommentBox) {
      const top = parseInt(this.newCommentBox.style.top) || 0;
      const height = this.newCommentBox.offsetHeight || 0;
      const bottom = top + height;

      if (bottom > maxBottom) {
        maxBottom = bottom;
      }
    }

    // Add some padding at the bottom
    const totalHeight = maxBottom + 30;

    // Only update and notify if the height has changed
    if (totalHeight !== this.totalCommentsHeight) {
      this.totalCommentsHeight = totalHeight;
      this.notifyHeightChange();
    }
  }

  /**
   * Notify all registered callbacks about height changes
   */
  notifyHeightChange() {
    for (const callback of this.heightChangeCallbacks) {
      try {
        callback(this.totalCommentsHeight);
      } catch (error) {
        Sentry.captureException(error, {
          tags: {
            notifyHeightChange: true,
            commentsManager: true,
            documentId: this.documentId,
          },
          extra: {
            documentRefExists: !!this.documentRef,
          },
        });
      }
    }
  }

  placeComment(element, top) {
    element.style.top = `${top}px`;
  }

  init(editorElement, documentRef, offset, documentId) {
    if (editorElement) {
      this.mutationObserver.observe(editorElement, {
        childList: true,
        subtree: true,
        characterData: true,
      });
      this.resizeObserver.observe(editorElement);
    }

    this.documentRef = documentRef || document;
    this.documentId = documentId;
    window.addEventListener("scroll", this.throttledUpdate);
    window.addEventListener("resize", this.throttledUpdate);
    this.offset = offset || 0;
  }

  destroy() {
    this.mutationObserver.disconnect();
    this.resizeObserver.disconnect();
    window.removeEventListener("scroll", this.throttledUpdate);
    window.removeEventListener("resize", this.throttledUpdate);
    this.comments.clear();
    this.newCommentBox = null;
    this.newCommentBoxY = null;
  }
}

// Create singleton instance
export const commentsManager = new CommentsManager();
